import React, { useState } from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import { ExternalLink, Loader2 } from 'lucide-react';
import { useData } from '../context/DataContext';

export const Interior = () => {
  const categories = ['All', 'Living Room', 'Bedroom', 'Kitchen', 'Bathroom', 'Office', 'Dining'];
  const [activeCategory, setActiveCategory] = useState('All');
  const { interiors: interiorProjects, loading } = useData();

  const filteredProjects = activeCategory === 'All'
    ? interiorProjects
    : interiorProjects.filter(project => project.category === activeCategory);

  const handleInquiry = () => {
    window.open('https://wa.me/918667644336?text=I am interested in interior design services', '_blank');
  };

  return (
    <section id="interior" className="py-24 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-12 animate-fade-in-up">
          <div className="inline-block bg-[#D4AF37]/10 text-[#D4AF37] px-4 py-2 rounded-full text-sm font-semibold mb-4">
            Interior Design
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-[#0F1419] mb-6 playfair">
            Transform Your Space
          </h2>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Award-winning interior design services that blend functionality with aesthetic excellence.
            Explore our portfolio of stunning transformations.
          </p>
        </div>

        {/* Category Tabs */}
        <Tabs defaultValue="All" className="mb-12">
          <TabsList className="flex flex-wrap justify-center gap-2 bg-transparent">
            {categories.map((category) => (
              <TabsTrigger
                key={category}
                value={category}
                onClick={() => setActiveCategory(category)}
                className="px-6 py-2 rounded-full data-[state=active]:bg-[#D4AF37] data-[state=active]:text-white border border-gray-200 hover:border-[#D4AF37] transition-colors"
              >
                {category}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>

        {/* Projects Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
          {loading ? (
            <div className="col-span-full flex justify-center py-20">
              <Loader2 className="w-12 h-12 text-[#D4AF37] animate-spin" />
            </div>
          ) : (
            <>
              {filteredProjects.map((project) => (
                <Card
                  key={project.id}
                  className="group overflow-hidden border-0 shadow-lg hover:shadow-2xl transition-all duration-300 smooth-hover cursor-pointer"
                  onClick={handleInquiry}
                >
                  {/* Image */}
                  <div className="relative image-zoom-container h-72">
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end">
                      <div className="p-6 text-white">
                        <Badge className="bg-[#D4AF37] text-white border-0 mb-3">
                          {project.category}
                        </Badge>
                        <h3 className="text-xl font-bold playfair mb-2">{project.title}</h3>
                        <p className="text-sm text-gray-200">{project.description}</p>
                      </div>
                    </div>
                    <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <div className="bg-white/90 backdrop-blur-sm p-2 rounded-full">
                        <ExternalLink className="w-5 h-5 text-[#D4AF37]" />
                      </div>
                    </div>
                  </div>

                  {/* Content (Mobile fallback) */}
                  <div className="p-6 md:hidden">
                    <Badge className="bg-[#D4AF37] text-white border-0 mb-3">
                      {project.category}
                    </Badge>
                    <h3 className="text-xl font-bold text-[#0F1419] playfair mb-2">{project.title}</h3>
                    <p className="text-sm text-gray-600">{project.description}</p>
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>

        {/* Service Features */}
        <div className="bg-gradient-to-br from-[#0F1419] to-[#2d3e50] rounded-3xl p-12 text-center">
          <h3 className="text-3xl font-bold text-white mb-6 playfair">
            Ready to Transform Your Space?
          </h3>
          <p className="text-gray-300 text-lg mb-8 max-w-2xl mx-auto">
            Our expert design team offers complete turnkey solutions including 3D visualization,
            space planning, material selection, and project execution.
          </p>
          <div className="flex flex-wrap justify-center gap-6 mb-8">
            <div className="flex items-center space-x-2 text-white">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
              <span>Free Consultation</span>
            </div>
            <div className="flex items-center space-x-2 text-white">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
              <span>3D Visualization</span>
            </div>
            <div className="flex items-center space-x-2 text-white">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
              <span>Turnkey Execution</span>
            </div>
            <div className="flex items-center space-x-2 text-white">
              <div className="w-2 h-2 bg-[#D4AF37] rounded-full"></div>
              <span>Premium Materials</span>
            </div>
          </div>
          <Button
            onClick={handleInquiry}
            size="lg"
            className="bg-[#D4AF37] hover:bg-[#B8941F] text-white px-8 py-6 text-lg font-semibold"
          >
            Schedule Free Consultation
          </Button>
        </div>
      </div>
    </section>
  );
};